import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/ui';

import styles from './StripePaymentForm.module.css';

export interface StripePaymentFormProps {
  /** Текст кнопки: цену формирует вызывающий экран — числа живут на сервере. */
  submitLabel: string;
  pendingLabel: string;
  /** Куда вернуть браузер, если банк уводит на свою страницу 3-D Secure. */
  returnUrl: string;
  /** Подтверждение прошло: экран обязан спросить сервер о результате. */
  onConfirmed: () => void;
  disabled?: boolean;
}

/**
 * Платёжная форма Stripe (FE-480, MVP_PRD §48).
 *
 * **Реквизиты карты вводятся не здесь.** `PaymentElement` — iframe в домене
 * платёжной системы: номер карты, срок и CVC не попадают ни в наш DOM, ни в
 * наш JavaScript, ни в наши запросы. Именно на этом держится граница PCI DSS
 * SAQ A, и собственных полей карты рядом быть не может.
 *
 * **3-D Secure показывает Stripe сам.** `redirect: 'if_required'` означает:
 * если эмитент требует подтверждения, оно происходит в модальном окне поверх
 * страницы и человек остаётся здесь; если способ оплаты требует настоящего
 * перехода на сайт банка — браузер уходит и возвращается по `return_url`.
 * Обе ветки заканчиваются одним и тем же: экран спрашивает **сервер**, что
 * стало с платежом.
 *
 * **Результат подтверждения — не источник правды.** Ответ этой формы означает
 * только «браузер дошёл до конца»; оплачен заказ или нет, решает событие
 * платёжной системы. Поэтому `onConfirmed` не сообщает об успехе, а лишь
 * говорит экрану, что пора спросить сервер.
 */
export function StripePaymentForm({
  submitLabel,
  pendingLabel,
  returnUrl,
  onConfirmed,
  disabled = false,
}: StripePaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const busy = pending || disabled || !stripe || !elements;

  async function submit() {
    if (!stripe || !elements) return;

    setPending(true);
    setError(null);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // Остаться на странице везде, где это возможно: уход на внешний адрес и
      // возврат по редиректу теряет состояние экрана, а у карт он не нужен.
      redirect: 'if_required',
    });

    setPending(false);

    if (result.error) {
      // Текст ошибки приходит от платёжной системы и уже локализован ею по
      // локали Elements. Подменять его своим («карта не прошла») нельзя:
      // причина бывает конкретной, и человеку нужна именно она.
      setError(result.error.message ?? t('payment.stripe.genericError'));
      return;
    }

    onConfirmed();
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        // Обработчик асинхронный, а атрибут формы ждёт `void`: без обёртки
        // необработанное отклонение промиса терялось бы молча.
        void submit();
      }}
      noValidate
    >
      <PaymentElement
        onReady={() => {
          setReady(true);
        }}
        options={{
          layout: 'tabs',
          // Адрес плательщика нужен счёту по § 14 Abs. 4 UStG. Собирает его
          // платёжная форма, а не наша: вторая форма адреса означала бы второй
          // ввод тех же данных и расхождение между счётом и тем, что видел банк.
          fields: { billingDetails: { address: 'auto', name: 'auto' } },
        }}
      />

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <Button type="submit" className={styles.submit} disabled={busy || !ready}>
        {pending ? pendingLabel : submitLabel}
      </Button>
    </form>
  );
}
